export type DeviceStatus = 'online' | 'offline' | 'pairing' | 'revoked';

export type StatusPresentation = {
  pip: 'ok' | 'warn' | 'danger';
  label: string;
};

/** BUILD.md §7 / style.md — status colour + an always-present text label. */
export const STATUS_PRESENTATION: Record<DeviceStatus, StatusPresentation> = {
  online: { pip: 'ok', label: 'Online' },
  offline: { pip: 'warn', label: 'Offline' },
  pairing: { pip: 'warn', label: 'Pairing' },
  revoked: { pip: 'danger', label: 'Revoked' }
};

export function statusPresentation(status: string | undefined): StatusPresentation {
  return STATUS_PRESENTATION[(status as DeviceStatus) ?? 'offline'] ?? STATUS_PRESENTATION.offline;
}

/** Compact class marker (plain words — the glossary bans "TAB"). */
export function deviceClassMark(deviceClass: string | undefined): string {
  return deviceClass === 'esp32' ? 'ESP32' : 'Tablet';
}

/**
 * Plain lockdown badge (FB-0.8 / CC-6). Word map from docs/GLOSSARY.md
 * "Use in the UI": pwa → Basic, shell → Secured, dpc → Locked (dpc is the
 * future native-lockdown tier; the capability enum today is none|pwa|shell).
 * `none` — no kiosk lockdown, e.g. the ESP32 thin client — and any unknown or
 * missing value read as the plainest badge. An un-locked device must never be
 * labelled "Locked". Lives here so the list and the detail panel cannot drift.
 */
export function lockdownLabel(lockdown: string | undefined): string {
  if (lockdown === 'pwa') return 'Basic';
  if (lockdown === 'shell') return 'Secured';
  if (lockdown === 'dpc') return 'Locked';
  return 'Basic';
}

export function relativeTime(iso: string | undefined): string {
  if (!iso) return 'never';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'never';
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
